import { useEffect, useRef, useState } from "react";
import { styles } from "../styles/styles";
import { supabase } from "../lib/supabaseClient";

import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ScrollToTopButton from "../components/layout/ScrollToTopButton";

import HeroSection from "../components/sections/HeroSection";
import AboutSection from "../components/sections/AboutSection";
import DogsSection from "../components/sections/DogsSection";
import NewsSection from "../components/sections/NewsSection";
import PuppiesSection from "../components/sections/PuppiesSection";
import MembershipSection from "../components/sections/MembershipSection";
import ContactSection from "../components/sections/ContactSection";
import SiteNoticeBanner from "../components/layout/SiteNoticeBanner";

export default function HomePage({
                                     kennelName,
                                     primaryColor,
                                     secondaryColor,
                                     accentColor: _accentColor,
                                     sliderInterval,

                                     sliderImages = [],
                                     elternTiere = [],
                                     welpenWurf,
                                     welpenWuerfe = [],

                                     huendinnen = [],
                                     rueden = [],

                                     news,
                                 }) {
    const [activeNavItem, setActiveNavItem] = useState("home");
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [legalOpen, setLegalOpen] = useState(null);

    const [newsItems, setNewsItems] = useState(news || []);
    const [isLoadingNews, setIsLoadingNews] = useState(false);

    const homeRef = useRef(null);
    const aboutRef = useRef(null);
    const dogsRef = useRef(null);
    const newsRef = useRef(null);
    const puppiesRef = useRef(null);
    const membershipRef = useRef(null);
    const contactRef = useRef(null);

    useEffect(() => {
        async function loadNews() {
            setIsLoadingNews(true);

            const { data, error } = await supabase
                .from("news")
                .select("*")
                .eq("published", true)
                .order("published_at", { ascending: false });

            if (error) {
                console.error("Fehler beim Laden der News:", error);
                setIsLoadingNews(false);
                return;
            }

            const mappedNews = data.map((item) => ({
                id: item.id,
                title: item.title,
                date: item.published_at
                    ? new Date(item.published_at).toLocaleDateString("de-DE", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                    })
                    : "Neuigkeit",
                text: item.excerpt || item.content || "",
                content: item.content || item.excerpt || "",
                buttonLabel: "Mehr lesen",
            }));

            setNewsItems(mappedNews);
            setIsLoadingNews(false);
        }

        loadNews();
    }, []);

    const scrollToSection = (sectionRef, navItem) => {
        sectionRef?.current?.scrollIntoView({ behavior: "smooth" });
        if (navItem) setActiveNavItem(navItem);
        setIsMenuOpen(false);
    };

    const navItems = [
        { key: "home", label: "Home", ref: homeRef },
        { key: "about", label: "Über uns", ref: aboutRef },
        { key: "dogs", label: "Hündinnen & Rüden", ref: dogsRef },
        { key: "news", label: "Neuigkeiten", ref: newsRef },

        { key: "litter-a", label: "A-Wurf", ref: "/a-wurf" },

        { key: "puppies", label: "A-Wurf (Home)", ref: puppiesRef },

        { key: "membership", label: "Mitgliedschaften", ref: membershipRef },
        { key: "contact", label: "Kontakt", ref: contactRef },
    ];

    return (
        <div style={styles.container}>
            <Header
                kennelName={kennelName}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                activeNavItem={activeNavItem}
                isMenuOpen={isMenuOpen}
                onToggleMenu={() => setIsMenuOpen((value) => !value)}
                navItems={navItems}
                onNavClick={(ref, key) => scrollToSection(ref, key)}
                styles={styles}
            />

            <SiteNoticeBanner primaryColor={primaryColor} />

            <div ref={homeRef}>
                <HeroSection
                    images={sliderImages}
                    interval={sliderInterval}
                    styles={styles}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    littersRef={puppiesRef}
                    contactRef={contactRef}
                />
            </div>

            <div ref={aboutRef}>
                <AboutSection
                    kennelName={kennelName}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    onCtaClick={() => scrollToSection(contactRef, "contact")}
                    styles={styles}
                />
            </div>

            <div ref={dogsRef}>
                <DogsSection
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    huendinnen={huendinnen}
                    rueden={rueden}
                    styles={styles}
                    onDogClick={(dog) => console.log("Dog clicked:", dog)}
                />
            </div>

            <div ref={newsRef}>
                <NewsSection
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    styles={styles}
                    news={newsItems}
                    isLoading={isLoadingNews}
                />
            </div>

            <div ref={puppiesRef}>
                <PuppiesSection
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    welpenWurf={welpenWurf}
                    welpenWuerfe={welpenWuerfe}
                    elternTiere={elternTiere}
                    styles={styles}
                    onPuppyInquiry={() => scrollToSection(contactRef, "contact")}
                />
            </div>

            <div ref={membershipRef}>
                <MembershipSection primaryColor={primaryColor} styles={styles} />
            </div>

            <div ref={contactRef}>
                <ContactSection
                    kennelName={kennelName}
                    primaryColor={primaryColor}
                    styles={styles}
                    onOpenPrivacy={() => setLegalOpen("datenschutz")}
                />
            </div>

            <Footer
                kennelName={kennelName}
                primaryColor={primaryColor}
                navItems={navItems}
                onNavClick={(ref, key) => scrollToSection(ref, key)}
                styles={styles}
                onOpenLegal={(type) => setLegalOpen(type)}
                legalOpen={legalOpen}
                onCloseLegal={() => setLegalOpen(null)}
            />

            <ScrollToTopButton
                primaryColor={primaryColor}
                onClick={() => scrollToSection(homeRef, "home")}
            />
        </div>
    );
}